import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type AvaliacaoGoogle = { texto: string; autor: string; nota: number };

/**
 * Depoimentos da /lp/: avaliações reais do perfil do Google, apenas as
 * selecionadas no painel /admin/avaliacoes/. Sem seleção, a seção não
 * renderiza. Mesma fonte de dados da home.
 */
export const getAvaliacoesLp = createServerFn({ method: "GET" }).handler(
  async (): Promise<AvaliacaoGoogle[]> => {
    const supabase = await (await import("@/lib/supabase-publico.server")).clientePublico();

    const { data } = await supabase
      .from("google_reviews")
      .select("author_name, review_text, rating")
      .eq("selected", true)
      .order("reviewed_at", { ascending: false, nullsFirst: false })
      .limit(6);

    return (data ?? []).map((avaliacao) => ({
      texto: avaliacao.review_text,
      autor: avaliacao.author_name,
      nota: avaliacao.rating,
    }));
  },
);

const contatoSchema = z.object({
  nome: z.string().trim().min(1, "Informe o nome.").max(120),
  telefone: z.string().trim().min(8, "Informe um telefone válido com DDD.").max(30),
  email: z.string().trim().email("Informe um e-mail válido.").max(255),
  assunto: z.string().trim().min(1, "Informe o assunto.").max(200),
  mensagem: z.string().trim().max(2000).optional(),
});

export type EnvioContatoResultado = { ok: boolean; erro?: string };

/**
 * Formulário de contato da /lp/: valida, grava o lead na tabela lp_leads
 * (visível no painel em /admin/contatos/) e aciona o envio por e-mail.
 * O e-mail é enviado depois pelo servidor; falha de envio não perde o lead.
 */
export const enviarContatoLp = createServerFn({ method: "POST" })
  .inputValidator((data) => contatoSchema.parse(data))
  .handler(async ({ data }): Promise<EnvioContatoResultado> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: lead, error } = await supabaseAdmin
      .from("lp_leads")
      .insert({
        nome: data.nome,
        telefone: data.telefone,
        email: data.email,
        assunto: data.assunto,
        mensagem: data.mensagem ?? null,
      })
      .select("id")
      .single();

    if (error || !lead) {
      console.error("Falha ao registrar contato da /lp/", error);
      return { ok: false, erro: "Não foi possível enviar sua mensagem agora. Tente novamente." };
    }

    const { enviarEmailContato } = await import("@/lib/lp.server");
    const dadosEmail = data.mensagem ? { ...data, mensagem: data.mensagem } : { ...data, mensagem: undefined };
    const emailOk = await enviarEmailContato(lead.id, dadosEmail).catch((erro: unknown) => {
      console.error("Falha ao enviar e-mail de contato da /lp/", erro);
      return false;
    });

    return emailOk ? { ok: true } : { ok: true, erro: "mensagem_registrada" };
  });
